/**
 * SellerResponseBadge — live "typically replies in 12 min" chip that
 * sellers can flash on their own dashboard and buyers can see on any
 * product detail page.
 *
 * Data source:
 *   GET /api/direct-chat/seller/response-stats             (own stats)
 *   GET /api/direct-chat/seller/response-stats/{workspace} (any seller)
 *
 * Tiers (server side):
 *   lightning < 5m · fast < 15m · steady < 60m · slow ≥ 60m · unknown
 */
import * as React from "react";
import { Zap, Clock3, Timer, Turtle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

type Tier = "lightning" | "fast" | "steady" | "slow" | "unknown";

interface Stats {
  median_seconds: number | null;
  target_seconds: number;
  tier: Tier;
  replies_count: number;
  threads_sampled: number;
  beats_target?: boolean;
}

const TIER_META: Record<Tier, { label: string; tone: string; icon: React.ComponentType<{ className?: string }>; hint: string }> = {
  lightning: { label: "Lightning replies", tone: "emerald", icon: Zap,      hint: "Typically replies in a few minutes" },
  fast:      { label: "Fast replies",      tone: "emerald", icon: Sparkles, hint: "Replies within 15 minutes on average" },
  steady:    { label: "Steady replies",    tone: "amber",   icon: Timer,    hint: "Replies within an hour on average" },
  slow:      { label: "Replies slowly",    tone: "slate",   icon: Turtle,   hint: "May take longer than an hour" },
  unknown:   { label: "New seller",        tone: "slate",   icon: Clock3,   hint: "Not enough chats to score yet" },
};

function formatMedian(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 60 * 60) return `${Math.round(seconds / 60)} min`;
  const h = seconds / 3600;
  return `${h < 10 ? h.toFixed(1) : Math.round(h)} h`;
}

export const SellerResponseBadge: React.FC<{
  workspaceId?: string;      // omitted → own stats
  size?: "sm" | "md";
  showHint?: boolean;
}> = ({ workspaceId, size = "sm", showHint = false }) => {
  const [stats, setStats] = React.useState<Stats | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const path = workspaceId
          ? `/direct-chat/seller/response-stats/${workspaceId}`
          : "/direct-chat/seller/response-stats";
        const r = await api.get<Stats>(path);
        if (alive) setStats(r.data);
      } catch {
        if (alive) setStats(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [workspaceId]);

  if (loading || !stats) return null;
  const meta = TIER_META[stats.tier] ?? TIER_META.unknown;
  const Icon = meta.icon;

  const toneCls =
    meta.tone === "emerald" ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-500/10 dark:text-emerald-300"
      : meta.tone === "amber" ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-300"
      : "border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300";

  const cls = size === "md" ? "text-[12px] px-3 py-1.5" : "text-[11px] px-2.5 py-1";
  const iconCls = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <div
      data-testid={`seller-response-badge-${stats.tier}`}
      title={meta.hint}
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${cls} ${toneCls}`}
    >
      <Icon className={iconCls} />
      <span>{meta.label}</span>
      {stats.median_seconds != null && (
        <>
          <span className="opacity-50">·</span>
          <span className="tabular-nums" data-testid="seller-response-median">
            {formatMedian(stats.median_seconds)}
          </span>
        </>
      )}
      {showHint && (
        <span className="opacity-60 pl-1 hidden md:inline">{meta.hint}</span>
      )}
    </div>
  );
};

export default SellerResponseBadge;
